import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, CheckCircle, AlertCircle, Loader, User, Briefcase, GraduationCap, Code, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import { apiClient } from '../../lib/api/client';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { AnalysisStatusMessage } from '../ui/AnalysisStatusMessage';
import { useAnalysisStatus } from '../../hooks/useAnalysisStatus';
// import { Textarea } from '../ui/Textarea';
// import { Select } from '../ui/Select';

// Form validation schema
const candidateSchema = z.object({
  // Basic Information (mandatory fields)
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone is required'),
  location: z.string().min(1, 'Location is required'),
  
  // Professional Information
  summary: z.string().optional(),
  current_position: z.string().optional(),
  current_company: z.string().optional(),
  experience_years: z.number().min(0).optional(),
  
  // Skills
  technical_skills: z.array(z.string()).optional(),
  soft_skills: z.array(z.string()).optional(),
  programming_languages: z.array(z.string()).optional(),
  frameworks: z.array(z.string()).optional(),
  databases: z.array(z.string()).optional(),
  cloud_platforms: z.array(z.string()).optional(),
  tools: z.array(z.string()).optional(),
  
  // Education
  education: z.array(z.object({
    degree: z.string(),
    field: z.string(),
    institution: z.string(),
    year: z.string()
  })).optional(),
  
  // Work Experience
  work_experience: z.array(z.object({
    company: z.string(),
    position: z.string(),
    duration: z.string(),
    description: z.string(),
    technologies: z.array(z.string()),
    achievements: z.array(z.string())
  })).optional(),
  
  // Certifications
  certifications: z.array(z.object({
    name: z.string(),
    issuer: z.string(),
    year: z.string()
  })).optional(),
  
  // Projects
  projects: z.array(z.object({
    name: z.string(),
    description: z.string(),
    technologies: z.array(z.string()),
    impact: z.string()
  })).optional(),
  
  // Social Profiles
  linkedin: z.string().optional(),
  github: z.string().optional(),
  website: z.string().optional(),
  
  // Preferences
  work_mode: z.array(z.string()).optional(),
  job_types: z.array(z.string()).optional(),
  target_titles: z.array(z.string()).optional(),
  relocation_willing: z.boolean().optional(),
  preferred_locations: z.array(z.string()).optional(),
  notice_period_days: z.number().optional(),
  earliest_start_date: z.string().optional(),
  
  // Compensation
  desired_currency: z.string().optional(),
  desired_base: z.number().optional(),
  desired_bonus: z.number().optional(),
  desired_equity: z.string().optional(),
  
  // Work Authorization
  work_auth_countries: z.array(z.string()).optional(),
  needs_sponsorship: z.boolean().optional(),
  visa_status: z.string().optional()
});

type CandidateFormData = z.infer<typeof candidateSchema> & {
  candidate_id?: string;
  fast_ai_data?: {
    cv_text?: string;
    parsed_data?: any;
  };
};

interface ComprehensiveCandidateFormProps {
  onSuccess: (candidate: any) => void;
  onCancel: () => void;
  editData?: any;
  mode?: 'create' | 'edit';
  requirementId?: string; // For JD vs CV analysis
}

// Removed unused ParsedData interface

export const ComprehensiveCandidateForm: React.FC<ComprehensiveCandidateFormProps> = ({
  onSuccess,
  onCancel,
  editData,
  mode = 'create',
  requirementId
}) => {
  const [formData, setFormData] = useState<CandidateFormData>({
    name: '',
    email: '',
    phone: '',
    location: '',
    summary: '',
    current_position: '',
    current_company: '',
    experience_years: 0,
    technical_skills: [],
    soft_skills: [],
    programming_languages: [],
    frameworks: [],
    databases: [],
    cloud_platforms: [],
    tools: [],
    education: [],
    work_experience: [],
    certifications: [],
    projects: [],
    linkedin: '',
    github: '',
    website: '',
    work_mode: [],
    job_types: [],
    target_titles: [],
    relocation_willing: false,
    preferred_locations: [],
    notice_period_days: 30,
    earliest_start_date: '',
    desired_currency: 'USD',
    desired_base: 0,
    desired_bonus: 0,
    desired_equity: '',
    work_auth_countries: [],
    needs_sponsorship: false,
    visa_status: ''
  });

  const [uploadStatus, setUploadStatus] = useState<{
    status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
    message: string;
    progress?: number;
  }>({
    status: 'idle',
    message: 'Upload a resume to auto-fill the form'
  });

  // const [candidateId, setCandidateId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Analysis status hook
  const { comprehensiveStatus, jdCvStatus } = useAnalysisStatus({
    candidateId: formData.candidate_id,
    requirementId,
    enabled: !!formData.candidate_id
  });

  // Initialize form with edit data
  useEffect(() => {
    if (editData && mode === 'edit') {
      setFormData({
        name: editData.name || '',
        email: editData.email || '',
        phone: editData.phone || '',
        location: editData.location || '',
        summary: editData.summary || '',
        current_position: editData.current_position || '',
        current_company: editData.current_company || '',
        experience_years: editData.experience_years || 0,
        technical_skills: editData.technical_skills || [],
        soft_skills: editData.soft_skills || [],
        programming_languages: editData.programming_languages || [],
        frameworks: editData.frameworks || [],
        databases: editData.databases || [],
        cloud_platforms: editData.cloud_platforms || [],
        tools: editData.tools || [],
        education: editData.education || [],
        work_experience: editData.work_experience || [],
        certifications: editData.certifications || [],
        projects: editData.projects || [],
        linkedin: editData.linkedin || '',
        github: editData.github || '',
        website: editData.website || '',
        work_mode: editData.work_mode || [],
        job_types: editData.job_types || [],
        target_titles: editData.target_titles || [],
        relocation_willing: editData.relocation_willing || false,
        preferred_locations: editData.preferred_locations || [],
        notice_period_days: editData.notice_period_days || 30,
        earliest_start_date: editData.earliest_start_date || '',
        desired_currency: editData.desired_currency || 'USD',
        desired_base: editData.desired_base || 0,
        desired_bonus: editData.desired_bonus || 0,
        desired_equity: editData.desired_equity || '',
        work_auth_countries: editData.work_auth_countries || [],
        needs_sponsorship: editData.needs_sponsorship || false,
        visa_status: editData.visa_status || ''
      });
    }
  }, [editData, mode]);

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      // Additional client-side validation for file extension
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.pdf', '.docx'];
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and DOCX files are allowed');
        return;
      }
      
      handleResumeUpload(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    onDropRejected: (fileRejections: any[]) => {
      const rejection = fileRejections[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        toast.error('File size cannot exceed 10MB');
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        toast.error('Only PDF and DOCX files are allowed');
      } else {
        toast.error('File upload failed');
      }
    }
  });

  const handleResumeUpload = async (file: File) => {
    setUploadStatus({
      status: 'uploading',
      message: 'Uploading resume...',
      progress: 0
    });

    try {
      const response = await apiClient.uploadCV(file, undefined, requirementId);
      // setCandidateId(response.candidate_id);
      setUploadStatus({
        status: 'processing',
        message: 'AI is processing your resume...',
        progress: 20
      });

      // Start polling for processing status
      pollProcessingStatus(response.candidate_id);
    } catch (error: any) {
      setUploadStatus({
        status: 'error',
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Failed to upload resume');
    }
  };

  const pollProcessingStatus = async (candidateId: string) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;

    const poll = async () => {
      try {
        const statusResponse = await apiClient.getResumeProcessingStatus(candidateId);
        
        if (statusResponse.status === 'completed') {
          setUploadStatus({
            status: 'completed',
            message: 'Resume processing completed!',
            progress: 100
          });
          
          // Auto-fill form with parsed data
          if (statusResponse.parsed_fields) {
            console.log('Auto-filling form with data:', statusResponse.parsed_fields);
            autoFillForm(statusResponse.parsed_fields);
          } else {
            console.log('No parsed_fields found in response:', statusResponse);
          }
          
          toast.success('Resume processed successfully! Form has been auto-filled.');
          return;
        } else if (statusResponse.status === 'failed') {
          setUploadStatus({
            status: 'error',
            message: statusResponse.message || 'Resume processing failed'
          });
          toast.error('Resume processing failed. Please try again.');
          return;
        } else if (attempts < maxAttempts) {
          attempts++;
          const progress = Math.min(20 + (attempts * 2), 90);
          setUploadStatus(prev => ({
            ...prev,
            message: `Processing resume... (${attempts}/${maxAttempts})`,
            progress
          }));
          
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setUploadStatus({
            status: 'error',
            message: 'Processing timeout. Please try again.'
          });
          toast.error('Processing timeout. Please try again.');
        }
      } catch (error) {
        console.error('Error polling resume status:', error);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 10000);
        } else {
          setUploadStatus({
            status: 'error',
            message: 'Failed to check processing status'
          });
          toast.error('Failed to check processing status');
        }
      }
    };

    setTimeout(poll, 5000); // Start polling after 5 seconds
  };

  const autoFillForm = (parsedData: any) => {
    // Handle new JD vs CV analysis format - data is directly in parsedData
    const formData = parsedData || {};
    const skills = formData.skills || [];
    const workExperience = formData.work_experience || [];
    const summary = formData.summary || '';

    // Basic information
    const name = formData.name || '';
    const email = formData.email || '';
    const phone = formData.phone || '';
    const currentPosition = formData.current_position || '';
    const currentCompany = formData.current_company || '';
    const experienceYears = formData.experience_years || 0;

    // Categorize skills into different types
    const technicalSkills = skills.filter((skill: string) => 
      !['JavaScript', 'Python', 'Java', 'C#', 'C++', 'TypeScript', 'Go', 'Rust', 'PHP', 'Ruby', 'Swift', 'Kotlin'].includes(skill)
    );
    const programmingLanguages = skills.filter((skill: string) => 
      ['JavaScript', 'Python', 'Java', 'C#', 'C++', 'TypeScript', 'Go', 'Rust', 'PHP', 'Ruby', 'Swift', 'Kotlin'].includes(skill)
    );
    const frameworks = skills.filter((skill: string) => 
      skill.toLowerCase().includes('framework') || 
      ['React', 'Angular', 'Vue', 'Spring', 'Django', 'Express', 'Laravel', 'Rails'].includes(skill)
    );
    const databases = skills.filter((skill: string) => 
      skill.toLowerCase().includes('sql') || 
      ['MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Oracle', 'SQLite'].includes(skill)
    );
    const cloudPlatforms = skills.filter((skill: string) => 
      ['AWS', 'Azure', 'GCP', 'Google Cloud', 'Amazon Web Services', 'Microsoft Azure'].includes(skill)
    );
    const tools = skills.filter((skill: string) => 
      ['Git', 'Docker', 'Kubernetes', 'Jenkins', 'Jira', 'Confluence', 'Slack'].includes(skill)
    );

    // Work experience
    const workExperienceData = workExperience.map((exp: any) => ({
      company: exp.company || '',
      position: exp.position || '',
      duration: exp.duration || '',
      description: exp.description || '',
      technologies: exp.technologies || [],
      achievements: []
    }));

    // Update form data
    const updatedFormData = {
      name: name || '',
      email: email || '',
      phone: phone || '',
      current_position: currentPosition || '',
      current_company: currentCompany || '',
      experience_years: experienceYears || 0,
      summary: summary || '',
      technical_skills: technicalSkills,
      programming_languages: programmingLanguages,
      frameworks: frameworks,
      databases: databases,
      cloud_platforms: cloudPlatforms,
      tools: tools,
      work_experience: workExperienceData,
      // Set candidate_id for form validation
      candidate_id: formData.candidate_id || ''
    };
    
    console.log('Auto-fill data being set:', updatedFormData);
    
    setFormData(prev => ({
      ...prev,
      ...updatedFormData
    }));

    // Mark form as having processed data for validation
    setFormData(prev => ({
      ...prev,
      fast_ai_data: {
        parsed_data: parsedData,
        processed_at: new Date().toISOString()
      }
    }));

    // Force a re-render to ensure form validation updates
    setTimeout(() => {
      console.log('Form data after auto-fill:', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      });
    }, 100);
  };

  const handleInputChange = (field: keyof CandidateFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleArrayInputChange = (field: keyof CandidateFormData, value: string) => {
    // const currentArray = (formData[field] as string[]) || [];
    const newArray = value.split(',').map(item => item.trim()).filter(Boolean);
    handleInputChange(field, newArray);
  };

  // Check if form is valid for submit button
  const isFormValid = formData.name && formData.email && formData.phone;
  
  // Debug logging
  console.log('Form validation check:', {
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    isFormValid
  });

  // Debug form data changes
  useEffect(() => {
    console.log('Form data updated:', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      hasFastAiData: !!formData.fast_ai_data
    });
  }, [formData.name, formData.email, formData.phone, formData.fast_ai_data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Check if required fields are filled
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error('Please fill in all required fields (Name, Email, Phone)');
        setErrors({ 
          name: !formData.name ? 'Name is required' : '',
          email: !formData.email ? 'Email is required' : '',
          phone: !formData.phone ? 'Phone is required' : ''
        });
        setIsSubmitting(false);
        return;
      }

      // Validate form data
      const validatedData = candidateSchema.parse(formData);
      
      // Prepare candidate data for submission
      const candidateData = {
        ...validatedData,
        // Convert arrays to comma-separated strings for API
        technical_skills: validatedData.technical_skills?.join(', ') || '',
        soft_skills: validatedData.soft_skills?.join(', ') || '',
        programming_languages: validatedData.programming_languages?.join(', ') || '',
        frameworks: validatedData.frameworks?.join(', ') || '',
        databases: validatedData.databases?.join(', ') || '',
        cloud_platforms: validatedData.cloud_platforms?.join(', ') || '',
        tools: validatedData.tools?.join(', ') || '',
        work_mode: validatedData.work_mode?.join(', ') || '',
        job_types: validatedData.job_types?.join(', ') || '',
        target_titles: validatedData.target_titles?.join(', ') || '',
        preferred_locations: validatedData.preferred_locations?.join(', ') || '',
        work_auth_countries: validatedData.work_auth_countries?.join(', ') || '',
        // Include Fast AI data and requirement ID for comprehensive analysis
        fast_ai_data: formData.fast_ai_data,
        requirement_id: requirementId
      };

      // Submit form data (non-blocking - triggers comprehensive analysis in background)
      const response = await apiClient.submitCandidateForm(candidateData);
      toast.success('Form submitted successfully! Comprehensive analysis will run in the background.');
      onSuccess(response);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        toast.error('Please fix the form errors');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit form');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus.status) {
      case 'uploading':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'processing':
        return <Loader className="w-5 h-5 text-yellow-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Upload className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Analysis Status Messages */}
      <AnalysisStatusMessage
        comprehensiveStatus={comprehensiveStatus || undefined}
        jdCvStatus={jdCvStatus || undefined}
      />
      
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {mode === 'edit' ? 'Edit Candidate' : 'Create New Candidate'}
              </h2>
              <p className="text-gray-600 mt-1">
                {mode === 'edit' ? 'Update candidate information' : 'Add a new candidate to your database'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                Cancel
              </Button>
              {mode === 'edit' && (
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={() => {
                    const form = document.querySelector('form');
                    if (form) (form as HTMLFormElement).requestSubmit();
                  }}
                  disabled={isSubmitting}
                >
                  Update
                </Button>
              )}
            </div>
          </div>

          {/* Resume Upload Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <File className="w-5 h-5 mr-2" />
              Resume Upload & Auto-Fill
            </h3>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                {isDragActive ? 'Drop the resume file here' : 'Drag & drop a resume file here'}
              </p>
              <p className="text-sm text-gray-500">
                or click to select a file
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Supports PDF, DOCX, DOC (max 10MB)
              </p>
            </div>

            {/* Upload Status */}
            {uploadStatus.status !== 'idle' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {getStatusIcon()}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {uploadStatus.message}
                    </p>
                    {uploadStatus.progress !== undefined && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadStatus.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Comprehensive Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Enter location"
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professional Summary
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => handleInputChange('summary', e.target.value)}
                  placeholder="Brief professional summary"
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.summary ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.summary && (
                  <p className="mt-1 text-sm text-red-600">{errors.summary}</p>
                )}
              </div>
            </div>

            {/* Skills Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Code className="w-5 h-5 mr-2" />
                Skills & Expertise
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Technical Skills
                  </label>
                  <textarea
                    value={formData.technical_skills?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('technical_skills', e.target.value)}
                    placeholder="Python, JavaScript, React, Node.js, AWS"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.technical_skills ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.technical_skills && (
                    <p className="mt-1 text-sm text-red-600">{errors.technical_skills}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Soft Skills
                  </label>
                  <textarea
                    value={formData.soft_skills?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('soft_skills', e.target.value)}
                    placeholder="Leadership, Communication, Problem Solving"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.soft_skills ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.soft_skills && (
                    <p className="mt-1 text-sm text-red-600">{errors.soft_skills}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Programming Languages
                  </label>
                  <textarea
                    value={formData.programming_languages?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('programming_languages', e.target.value)}
                    placeholder="Python, JavaScript, Java, C++"
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.programming_languages ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.programming_languages && (
                    <p className="mt-1 text-sm text-red-600">{errors.programming_languages}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frameworks & Libraries
                  </label>
                  <textarea
                    value={formData.frameworks?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('frameworks', e.target.value)}
                    placeholder="React, Django, Spring Boot, Express.js"
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.frameworks ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.frameworks && (
                    <p className="mt-1 text-sm text-red-600">{errors.frameworks}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Databases
                  </label>
                  <textarea
                    value={formData.databases?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('databases', e.target.value)}
                    placeholder="PostgreSQL, MongoDB, Redis, MySQL"
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.databases ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.databases && (
                    <p className="mt-1 text-sm text-red-600">{errors.databases}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cloud Platforms
                  </label>
                  <textarea
                    value={formData.cloud_platforms?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('cloud_platforms', e.target.value)}
                    placeholder="AWS, Azure, Google Cloud, Docker"
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.cloud_platforms ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.cloud_platforms && (
                    <p className="mt-1 text-sm text-red-600">{errors.cloud_platforms}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Dynamic CV Data Display */}
            {formData.fast_ai_data?.parsed_data && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mr-3">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    AI-Extracted Information
                  </h3>
                  <div className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                    Auto-filled
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 rounded-2xl p-8 shadow-lg">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Basic Information Card */}
                    <div className="bg-white rounded-xl p-6 shadow-md border border-green-100">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">Basic Information</h4>
                      </div>
                      <div className="space-y-3">
                        {formData.name && (
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Name</span>
                            <span className="text-sm font-semibold text-gray-900">{formData.name}</span>
                          </div>
                        )}
                        {formData.email && (
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Email</span>
                            <span className="text-sm font-semibold text-gray-900">{formData.email}</span>
                          </div>
                        )}
                        {formData.phone && (
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Phone</span>
                            <span className="text-sm font-semibold text-gray-900">{formData.phone}</span>
                          </div>
                        )}
                        {formData.current_position && (
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Position</span>
                            <span className="text-sm font-semibold text-gray-900">{formData.current_position}</span>
                          </div>
                        )}
                        {formData.current_company && (
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Company</span>
                            <span className="text-sm font-semibold text-gray-900">{formData.current_company}</span>
                          </div>
                        )}
                        {(formData.experience_years || 0) > 0 && (
                          <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg border border-blue-200">
                            <span className="text-sm font-medium text-blue-700">Experience</span>
                            <span className="text-sm font-bold text-blue-900">{formData.experience_years} years</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Skills Card */}
                    <div className="bg-white rounded-xl p-6 shadow-md border border-green-100">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                          <Code className="w-5 h-5 text-purple-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">Technical Skills</h4>
                      </div>
                      <div className="space-y-4">
                        {(formData.programming_languages?.length || 0) > 0 && (
                          <div>
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                              <span className="text-sm font-semibold text-gray-700">Programming Languages</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.programming_languages?.slice(0, 6).map((skill: string, idx: number) => (
                                <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border border-blue-200">
                                  {skill}
                                </span>
                              ))}
                              {(formData.programming_languages?.length || 0) > 6 && (
                                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                                  +{(formData.programming_languages?.length || 0) - 6} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {(formData.frameworks?.length || 0) > 0 && (
                          <div>
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              <span className="text-sm font-semibold text-gray-700">Frameworks & Libraries</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.frameworks?.slice(0, 4).map((skill: string, idx: number) => (
                                <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full border border-green-200">
                                  {skill}
                                </span>
                              ))}
                              {(formData.frameworks?.length || 0) > 4 && (
                                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                                  +{(formData.frameworks?.length || 0) - 4} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {(formData.databases?.length || 0) > 0 && (
                          <div>
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                              <span className="text-sm font-semibold text-gray-700">Databases</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.databases?.slice(0, 4).map((skill: string, idx: number) => (
                                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full border border-purple-200">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(formData.cloud_platforms?.length || 0) > 0 && (
                          <div>
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                              <span className="text-sm font-semibold text-gray-700">Cloud Platforms</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.cloud_platforms?.slice(0, 4).map((skill: string, idx: number) => (
                                <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full border border-orange-200">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Work Experience Card */}
                  {(formData.work_experience?.length || 0) > 0 && (
                    <div className="mt-6 bg-white rounded-xl p-6 shadow-md border border-green-100">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                          <Briefcase className="w-5 h-5 text-indigo-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">Work Experience</h4>
                      </div>
                      <div className="space-y-4">
                        {formData.work_experience?.slice(0, 3).map((exp: any, idx: number) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h5 className="font-semibold text-gray-900">{exp.position}</h5>
                                <p className="text-sm text-gray-600">{exp.company}</p>
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                                {exp.duration}
                              </span>
                            </div>
                            {exp.description && (
                              <p className="text-sm text-gray-700 leading-relaxed">{exp.description}</p>
                            )}
                            {exp.technologies && exp.technologies.length > 0 && (
                              <div className="mt-3">
                                <div className="flex flex-wrap gap-1">
                                  {exp.technologies.slice(0, 5).map((tech: string, techIdx: number) => (
                                    <span key={techIdx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                                      {tech}
                                    </span>
                                  ))}
                                  {exp.technologies.length > 5 && (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded border border-gray-200">
                                      +{exp.technologies.length - 5} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Professional Summary */}
                  {formData.summary && (
                    <div className="mt-6 bg-white rounded-xl p-6 shadow-md border border-green-100">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-3">
                          <File className="w-5 h-5 text-teal-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">Professional Summary</h4>
                      </div>
                      <p className="text-gray-700 leading-relaxed text-sm bg-gray-50 p-4 rounded-lg border border-gray-200">
                        {formData.summary}
                      </p>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-6 pt-4 border-t border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span className="font-medium">AI-powered extraction completed</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        You can edit any information above
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Work Experience */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Briefcase className="w-5 h-5 mr-2" />
                Work Experience
              </h3>
              
              {formData.work_experience?.map((exp, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <Input
                        value={exp.company}
                        onChange={(e) => {
                          const newExp = [...(formData.work_experience || [])];
                          newExp[index] = { ...exp, company: e.target.value };
                          handleInputChange('work_experience', newExp);
                        }}
                        placeholder="Company name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position
                      </label>
                      <Input
                        value={exp.position}
                        onChange={(e) => {
                          const newExp = [...(formData.work_experience || [])];
                          newExp[index] = { ...exp, position: e.target.value };
                          handleInputChange('work_experience', newExp);
                        }}
                        placeholder="Job title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration
                      </label>
                      <Input
                        value={exp.duration}
                        onChange={(e) => {
                          const newExp = [...(formData.work_experience || [])];
                          newExp[index] = { ...exp, duration: e.target.value };
                          handleInputChange('work_experience', newExp);
                        }}
                        placeholder="2020 - Present"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Technologies
                      </label>
                      <Input
                        value={exp.technologies.join(', ')}
                        onChange={(e) => {
                          const newExp = [...(formData.work_experience || [])];
                          newExp[index] = { 
                            ...exp, 
                            technologies: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                          };
                          handleInputChange('work_experience', newExp);
                        }}
                        placeholder="Python, React, AWS"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={exp.description}
                      onChange={(e) => {
                        const newExp = [...(formData.work_experience || [])];
                        newExp[index] = { ...exp, description: e.target.value };
                        handleInputChange('work_experience', newExp);
                      }}
                      placeholder="Describe your role and achievements"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const newExp = [...(formData.work_experience || [])];
                  newExp.push({
                    company: '',
                    position: '',
                    duration: '',
                    description: '',
                    technologies: [],
                    achievements: []
                  });
                  handleInputChange('work_experience', newExp);
                }}
              >
                Add Work Experience
              </Button>
            </div>

            {/* Education */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <GraduationCap className="w-5 h-5 mr-2" />
                Education
              </h3>
              
              {formData.education?.map((edu, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Degree
                      </label>
                      <Input
                        value={edu.degree}
                        onChange={(e) => {
                          const newEdu = [...(formData.education || [])];
                          newEdu[index] = { ...edu, degree: e.target.value };
                          handleInputChange('education', newEdu);
                        }}
                        placeholder="Bachelor of Science"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field of Study
                      </label>
                      <Input
                        value={edu.field}
                        onChange={(e) => {
                          const newEdu = [...(formData.education || [])];
                          newEdu[index] = { ...edu, field: e.target.value };
                          handleInputChange('education', newEdu);
                        }}
                        placeholder="Computer Science"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Institution
                      </label>
                      <Input
                        value={edu.institution}
                        onChange={(e) => {
                          const newEdu = [...(formData.education || [])];
                          newEdu[index] = { ...edu, institution: e.target.value };
                          handleInputChange('education', newEdu);
                        }}
                        placeholder="University name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year
                      </label>
                      <Input
                        value={edu.year}
                        onChange={(e) => {
                          const newEdu = [...(formData.education || [])];
                          newEdu[index] = { ...edu, year: e.target.value };
                          handleInputChange('education', newEdu);
                        }}
                        placeholder="2020"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const newEdu = [...(formData.education || [])];
                  newEdu.push({
                    degree: '',
                    field: '',
                    institution: '',
                    year: ''
                  });
                  handleInputChange('education', newEdu);
                }}
              >
                Add Education
              </Button>
            </div>

            {/* Social Profiles */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                Social Profiles
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn Profile
                  </label>
                  <Input
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/username"
                  />
                  {errors.linkedin && (
                    <p className="mt-1 text-sm text-red-600">{errors.linkedin}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GitHub Profile
                  </label>
                  <Input
                    value={formData.github}
                    onChange={(e) => handleInputChange('github', e.target.value)}
                    placeholder="https://github.com/username"
                  />
                  {errors.github && (
                    <p className="mt-1 text-sm text-red-600">{errors.github}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Website
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className={`min-w-[120px] ${
                  !isFormValid && !isSubmitting 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:shadow-lg transition-all duration-200'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    {mode === 'edit' ? 'Updating...' : 'Creating...'}
                  </div>
                ) : !isFormValid ? (
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Fill Required Fields
                  </div>
                ) : (
                  mode === 'edit' ? 'Update Candidate' : 'Create Candidate'
                )}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};
